import Link from "next/link";
import { Sparkles, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * "Can't find it? We make bespoke to order" prompt for /products, surfaced high
 * up so shoppers see it without scrolling past the whole grid. One component,
 * three placements (shared copy + styling):
 *  - `bar`   — slim full-width banner under the hero (both desktop + mobile).
 *  - `card`  — compact card pinned at the foot of the sticky desktop filter
 *              sidebar (persistent as the grid scrolls).
 *  - `empty` — the no-results state (the highest-intent "can't find it" moment).
 *
 * Presentational only (no hooks / no "use client"), so it renders as a Server
 * Component in page.tsx AND inside the client grid/sidebar. CTA → /enquire,
 * which aggregates every contact channel.
 */
export function CustomOrderPrompt({ variant }: { variant: "bar" | "card" | "empty" }) {
  if (variant === "card") {
    return (
      <div className="mt-3 shrink-0 rounded-lg border border-primary/25 bg-primary/5 p-3 text-center">
        <Sparkles className="mx-auto h-4 w-4 text-primary" aria-hidden="true" />
        <p className="mt-1.5 text-sm font-semibold text-foreground">Can&apos;t find it?</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Every arrangement is made to order — we&apos;ll design something bespoke.
        </p>
        <Button asChild size="sm" className="mt-2.5 w-full">
          <Link href="/enquire">
            <MessageCircle className="h-3.5 w-3.5" />
            Ask us
          </Link>
        </Button>
      </div>
    );
  }

  if (variant === "empty") {
    return (
      <div className="mx-auto max-w-md rounded-xl border border-primary/25 bg-primary/5 p-6 text-center">
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
          <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
        </div>
        <h3 className="mt-3 heading-card">No matches — but we make bespoke to order</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Tell us the occasion, the colours you have in mind, your budget, or even
          just a reference image — we&apos;ll happily design something around it.
        </p>
        <Button asChild size="lg" className="mt-4 w-full sm:w-auto">
          <Link href="/enquire">
            <MessageCircle className="h-4 w-4" />
            Ask us for a custom arrangement
          </Link>
        </Button>
      </div>
    );
  }

  // "bar"
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-primary/25 bg-mist/50 px-4 py-3 text-center sm:flex-row sm:justify-between sm:text-left">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
        </span>
        <p className="text-sm text-foreground">
          <span className="font-semibold">If you can&apos;t find one you love,</span>{" "}
          <span className="text-muted-foreground">
            every arrangement is made to order — tell us what you&apos;re after and
            we&apos;ll design something bespoke.
          </span>
        </p>
      </div>
      <Button asChild size="sm" className="w-full shrink-0 sm:w-auto">
        <Link href="/enquire">
          <MessageCircle className="h-4 w-4" />
          Ask us
        </Link>
      </Button>
    </div>
  );
}
